/**
 * Non-gendered inverse pairs of the backend RelationType enum. The relations API returns rows
 * "<related> is <type> of this entity" (the mirrored half of a pair); flipping a row's type to its
 * inverse re-reads it from the current entity's perspective, which is how the form dialogs label
 * their chips (e.g. a "The North Contains me" row displays as "Located In: The North").
 *
 * Symmetric types (Friend, Enemy, Rival, AlliedWith, AssociatedWith, ...) and gender-resolved family
 * types are intentionally absent — they display correctly without flipping.
 */
const INVERSE_PAIRS: [string, string][] = [
	["Owner", "BelongsTo"],
	["PrimaryWeapon", "Wielder"],
	["LocatedIn", "Contains"],
	["HomeOf", "ResidesIn"],
	["SeatOf", "HasSeat"],
	["PartOfWorld", "WorldOf"],
	["StoredAt", "Stores"],
	["ForgedBy", "ForgerOf"],
	["GiftedTo", "RecipientOf"],
	["InheritedBy", "InheritorOf"],
	["OriginatesFrom", "OriginOf"],
	["BondedTo", "BondedWith"],
	["CompanionOf", "HasCompanion"],
	["BelongsToPack", "HasPackMember"],
	["SeenIn", "SightingOf"],
	["LedBy", "LeaderOf"],
	["Protects", "ProtectedBy"],
	["MemberOf", "HasMember"],
	["OccurredAt", "SiteOf"],
	["Involved", "InvolvedIn"],
	["TriggeredBy", "Triggered"],
	["ResultedIn", "ResultOf"],
	["PartOfEra", "EraIncludes"],
	["WitnessedBy", "Witnessed"],
	["NativeTo", "NativeLandOf"],
	["Inhabits", "HabitatOf"],
	["OpposedBy", "Opposes"],
	["AncestorOf", "DescendantOf"],
	["PracticedBy", "Practices"],
	["SymbolizedBy", "SymbolOf"],
	["Influences", "InfluencedBy"],
	["UsedBy", "UserOf"],
	["PartOf", "HasPart"],
	// Succession is directional, not symmetric: without this pair a row was left unflipped and the
	// same words ended up meaning opposite things on the view panel and in the edit dialog.
	["PredecessorOf", "SuccessorOf"]
];

const FLIP_MAP: Record<string, string> = Object.fromEntries(
	INVERSE_PAIRS.flatMap(([forward, inverse]: [string, string]) => [
		[forward, inverse],
		[inverse, forward]
	])
);

/** Flips a relation type to its inverse; returns the type unchanged when symmetric or gender-resolved. */
export const flipRelationPerspective = (relationType: string): string => FLIP_MAP[relationType] ?? relationType;
